/**
 * Maatcode-fotoherkenning: hetdakraam.nl (GOAL-036 fase 2)
 *
 * Config-gated: dit script wordt alleen ingeladen wanneer de maatfinder-sectie
 * een Worker-endpoint én een Turnstile site key heeft (sections/maatfinder.liquid).
 * De klant fotografeert het typeplaatje; de foto gaat verkleind (max 1568px,
 * canvas-herencode naar JPEG = EXIF/GPS gestript) naar de maatcode-vision
 * Worker (maatcode-worker/). Er wordt niets opgeslagen, ook niet client-side.
 *
 * Routing volgt het contract in maatcode-worker/README.md:
 *   - ok && herkend && inAssortiment && confidence >= 0.8 -> direct naar collection
 *     (nooit bij `omgerekend`: een ander merk gaat altijd via het bevestigscherm)
 *   - ok && herkend && confidence >= 0.4                  -> bevestigscherm
 *   - al het andere (incl. ok:false / netwerkfout)        -> drie-treden-fallback
 *     (nieuwe foto / handmatige B×H-invoer / contactpagina)
 */
(function () {
  "use strict";

  // 1568 = de lange zijde die Claude vision nog zelf benut (hub @1b220b, 18-09): op 1024 werd een
  // klein typeplaatje in een telefoonfoto onleesbaar. Body blijft ruim onder de 3 MB van de Worker.
  var MAX_DIMENSION = 1568;
  var JPEG_QUALITY = 0.85;
  var DIRECT_CONFIDENCE = 0.8;
  var CONFIRM_CONFIDENCE = 0.4;
  var MAX_FILE_BYTES = 30 * 1024 * 1024; // decode-guard voor mobiele browsers
  // Het pad MOET /v0/ bevatten: /turnstile/api.js geeft 404 met CORP same-origin, Chrome blokkeert
  // dat als NotSameOrigin en er komt nooit een token (gemeten 17-09-2026, hub 9d00cf).
  var TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  // 18-09-2026: in een door CDP bestuurde test-Chrome kwam het token pas na 12-25+ s en later
  // helemaal niet (Cloudflare-fout 300030 = bot vermoed). Dat is ons testharnas, geen bewijs over
  // klanten; toch starten we de check al zodra de widget in beeld komt (IntersectionObserver in
  // init) en wachten we ruim, want 12 s liet geen marge. De foutcode gaat mee als turnstile_fout.
  var TURNSTILE_TIMEOUT_MS = 45000;
  var TOKEN_TTL_MS = 240000; // Turnstile-tokens verlopen na 300s; ruim daarbinnen blijven
  var PREP_TIMEOUT_MS = 50000;  // foto verkleinen + Turnstile-token
  var FETCH_TIMEOUT_MS = 30000; // Worker-call
  var VELUX_CODE_RE = /^[A-Z]{1,3}[0-9]{2}$/;

  /* ---------- pure beslislogica (unit-testbaar) ---------- */

  function collectionUrl(result) {
    if (result.merk === "roto") return "/collections/roto";
    return "/collections/velux-" + encodeURIComponent(String(result.maatcode).toLowerCase());
  }

  function canRouteDirect(result) {
    // Alleen de twee bekende merken zijn routeerbaar; Velux-codes bovendien
    // alleen in het verwachte formaat (defense-in-depth bovenop de Worker-whitelist).
    if (result.merk === "roto") return true;
    if (result.merk === "velux") return VELUX_CODE_RE.test(String(result.maatcode));
    return false;
  }

  function isMerkwissel(result) {
    return !!(result && result.omgerekend && typeof result.omgerekend === "object");
  }

  function decideRoute(result) {
    if (!result || result.ok !== true || result.herkend !== true || !result.maatcode) {
      return { action: "fallback", reason: result && result.reason ? result.reason : "niet_herkend" };
    }
    var conf = typeof result.confidence === "number" ? result.confidence : 0;
    // Een ander merk (omgerekend naar het Velux-equivalent) gaat nooit direct door, ook niet bij
    // een hoge confidence: de klant moet de merkwissel en het nieuwe gootstuk eerst zien.
    if (result.inAssortiment === true && conf >= DIRECT_CONFIDENCE && canRouteDirect(result) && !isMerkwissel(result)) {
      return { action: "direct", url: collectionUrl(result) };
    }
    if (conf >= CONFIRM_CONFIDENCE) {
      return { action: "confirm" };
    }
    return { action: "fallback", reason: "lage_confidence" };
  }

  function resultLabel(result) {
    var merkLabel = result.merk === "roto" ? "Roto" : result.merk === "velux" ? "VELUX" : "";
    return [merkLabel, result.type, result.maatcode].filter(Boolean).join(" ");
  }

  var ANDER_MERK_LABEL = { fakro: "Fakro", keylite: "Keylite", dakea: "Dakea" };

  /** "Fakro 06 (78 × 118 cm)", "Solstro (78 × 118 cm)" of "een dakraam van 78 × 118 cm". */
  function vanLabel(om) {
    var maat = om.buitenmaat_cm ? om.buitenmaat_cm.breedte + " × " + om.buitenmaat_cm.hoogte + " cm" : "";
    var merk = ANDER_MERK_LABEL[om.van_merk] || om.van_merk_tekst || "";
    if (!merk) return maat ? "een dakraam van " + maat : "een dakraam van een ander merk";
    return [merk, om.van_code].filter(Boolean).join(" ") + (maat ? " (" + maat + ")" : "");
  }

  /** Teksten voor de bevestigstap bij een ander merk (pure functie, getest). */
  function merkwisselTekst(result) {
    var om = result.omgerekend || {};
    var doelMerk = result.merk === "roto" ? "Roto" : "VELUX";
    var zelfde = result.merk === "roto" ? "vrijwel dezelfde maat" : "dezelfde maat";
    return {
      vraag: "Op je typeplaatje staat " + vanLabel(om) + ". Het dakraam met " + zelfde + " is " +
        doelMerk + " " + result.maatcode + ". Klopt dit?",
      gootstuk: "Stap je over van merk, neem dan ook een nieuw " + doelMerk +
        "-gootstuk: het gootstuk van je oude raam is voor dat merk gemaakt.",
    };
  }

  var STANDAARD_TIP = "Tip: fotografeer het typeplaatje recht van voren, met de hele code scherp in beeld.";

  /** Eerlijke uitleg per reden: wat er WEL gelezen is, en wat de klant nu kan doen. */
  function fallbackTekst(reason, result) {
    var om = result && result.omgerekend;
    switch (reason) {
      case "rate_limited":
        return { intro: "Er zijn net iets te veel pogingen gedaan. Wacht een minuutje en probeer het opnieuw, of mail de foto naar info@hetdakraam.nl.", tip: "" };
      case "turnstile":
      case "voorbereiding_timeout":
        // De foto is nooit bij ons aangekomen: een fototip zou de klant de verkeerde kant op sturen.
        return {
          intro: "De beveiligingscheck van onze site kwam niet rond, dus je foto is nog niet verstuurd. Probeer het nog een keer. Lukt het dan niet, vul je maat handmatig in of mail de foto naar info@hetdakraam.nl.",
          tip: "",
        };
      case "geen_typeplaatje":
        return {
          intro: "We zien geen typeplaatje op deze foto. Het zit boven in het kozijn: open het raam en kijk bij de bovenrand.",
          tip: "Tip: open het raam en fotografeer het plaatje recht van voren.",
        };
      case "geen_maat":
        return {
          intro: "Op dit typeplaatje staat geen maat, of die is niet meer te lezen. Meet dan de dakopening en vul je maat handmatig in: dan tonen we welk dakraam past.",
          tip: "",
        };
      case "onscherp":
        return { intro: "De foto is te onscherp om de maat zeker te lezen. Met een nieuwe foto lukt het meestal wel:", tip: STANDAARD_TIP };
      case "ander_merk_geen_equivalent":
        return {
          intro: "Op je typeplaatje staat " + (om ? vanLabel(om) : "een dakraam van een ander merk") +
            ". Bij VELUX en Roto bestaat geen standaardmaat die daar precies op past. Meet de dakopening en vul je maat handmatig in, of vraag ons om hulp.",
          tip: "",
        };
      case "ander_merk_geen_maat":
        return {
          intro: "We zien een typeplaatje van " + (ANDER_MERK_LABEL[result && result.merk] || "een ander merk") +
            ", maar kunnen de maat er niet zeker van lezen. Zo kom je er alsnog:",
          tip: STANDAARD_TIP,
        };
      default:
        return { intro: "Het lukte niet om de code van deze foto te lezen. Zo kom je er alsnog:", tip: STANDAARD_TIP };
    }
  }

  /* ---------- analytics (zelfde patroon als maatfinder.js) ---------- */

  function trackEvent(eventName, params) {
    if (window.gtag) {
      // Lege velden (bv. van_merk bij een gewoon VELUX-plaatje) niet meesturen.
      var schoon = {};
      for (var k in params || {}) {
        if (params[k] !== undefined) schoon[k] = params[k];
      }
      window.gtag("event", eventName, schoon);
    }
  }

  /* ---------- Turnstile (lazy, fail-stil naar fallback-trede) ---------- */

  var turnstileState = { scriptPromise: null, widgetId: null, token: null, tokenIssuedAt: 0, waiters: [], lastError: null };

  function loadTurnstileScript() {
    if (turnstileState.scriptPromise) return turnstileState.scriptPromise;
    turnstileState.scriptPromise = new Promise(function (resolve, reject) {
      if (window.turnstile) { resolve(); return; }
      var s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () {
        // Niet permanent cachen: een transient failure (netwerk/adblock) mag
        // bij de volgende poging een verse load krijgen.
        turnstileState.scriptPromise = null;
        if (s.parentNode) s.parentNode.removeChild(s);
        reject(new Error("turnstile_script"));
      };
      document.head.appendChild(s);
    });
    return turnstileState.scriptPromise;
  }

  function currentToken() {
    if (turnstileState.token && Date.now() - turnstileState.tokenIssuedAt < TOKEN_TTL_MS) {
      return turnstileState.token;
    }
    return null;
  }

  function flushTokenWaiters(token) {
    var waiters = turnstileState.waiters.splice(0);
    waiters.forEach(function (w) {
      if (!w.active) return;
      w.active = false;
      clearTimeout(w.timer);
      w.resolve(token);
    });
  }

  function ensureTurnstileWidget(container, sitekey) {
    return loadTurnstileScript().then(function () {
      if (turnstileState.widgetId !== null) return;
      turnstileState.widgetId = window.turnstile.render(container, {
        sitekey: sitekey,
        appearance: "interaction-only",
        callback: function (token) {
          turnstileState.lastError = null;
          turnstileState.token = token;
          turnstileState.tokenIssuedAt = Date.now();
          flushTokenWaiters(token);
        },
        "expired-callback": function () {
          turnstileState.token = null;
          try { window.turnstile.reset(turnstileState.widgetId); } catch (e) { /* geen blocker */ }
        },
        "error-callback": function (code) {
          turnstileState.lastError = code ? String(code) : "onbekend";
          turnstileState.token = null;
          flushTokenWaiters(null);
        },
      });
    });
  }

  function getTurnstileToken() {
    var token = currentToken();
    if (token) return Promise.resolve(token);
    if (turnstileState.token) {
      // Token bestaat maar nadert zijn vervaltijd: reset voor een verse challenge.
      consumeTurnstileToken();
    }
    return new Promise(function (resolve) {
      var waiter = { resolve: resolve, active: true, timer: 0 };
      waiter.timer = setTimeout(function () {
        waiter.active = false;
        var idx = turnstileState.waiters.indexOf(waiter);
        if (idx !== -1) turnstileState.waiters.splice(idx, 1);
        resolve(null);
      }, TURNSTILE_TIMEOUT_MS);
      turnstileState.waiters.push(waiter);
    });
  }

  function consumeTurnstileToken() {
    // Turnstile-tokens zijn single-use: na elke POST resetten voor een vers token.
    turnstileState.token = null;
    turnstileState.tokenIssuedAt = 0;
    if (window.turnstile && turnstileState.widgetId !== null) {
      try { window.turnstile.reset(turnstileState.widgetId); } catch (e) { /* geen blocker */ }
    }
  }

  /* ---------- timeouts (HIGH-fix: nooit vastlopen zonder fallback-UI) ---------- */

  function withTimeout(promise, ms, reason) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        reject(Object.assign(new Error(reason), { reason: reason }));
      }, ms);
      promise.then(
        function (v) { clearTimeout(timer); resolve(v); },
        function (e) { clearTimeout(timer); reject(e); }
      );
    });
  }

  /* ---------- foto -> verkleinde JPEG-base64 (EXIF/GPS gestript) ---------- */

  function loadBitmap(file) {
    if (window.createImageBitmap) {
      // Bewust GEEN kale createImageBitmap(file)-fallback: dat pad negeert in
      // oudere browsers de EXIF-orientatie. Het <img>-pad past die wel toe.
      return createImageBitmap(file, { imageOrientation: "from-image" })
        .catch(function () { return loadViaImageElement(file); });
    }
    return loadViaImageElement(file);
  }

  function loadViaImageElement(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error("decode")); };
      img.src = url;
    });
  }

  function bitmapSize(bitmap) {
    return {
      w: bitmap.width || bitmap.naturalWidth,
      h: bitmap.height || bitmap.naturalHeight,
    };
  }

  function fileToJpegBase64(file) {
    return loadBitmap(file).then(function (bitmap) {
      var size = bitmapSize(bitmap);
      if (!size.w || !size.h) throw new Error("decode");
      var scale = Math.min(1, MAX_DIMENSION / Math.max(size.w, size.h));
      var w = Math.max(1, Math.round(size.w * scale));
      var h = Math.max(1, Math.round(size.h * scale));
      var canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
      if (bitmap.close) bitmap.close();
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (!blob) { reject(new Error("encode")); return; }
          var reader = new FileReader();
          reader.onload = function () {
            // dataURL = "data:image/jpeg;base64,...." -> alleen het base64-deel
            var dataUrl = String(reader.result);
            resolve(dataUrl.slice(dataUrl.indexOf(",") + 1));
          };
          reader.onerror = function () { reject(new Error("encode")); };
          reader.readAsDataURL(blob);
        }, "image/jpeg", JPEG_QUALITY);
      });
    });
  }

  /* ---------- Worker-call (met abort-timeout) ---------- */

  function postFoto(endpoint, base64, token) {
    var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS)
      : 0;
    var opts = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image: base64, mediaType: "image/jpeg", turnstileToken: token }),
    };
    if (controller) opts.signal = controller.signal;
    var p = fetch(endpoint, opts).then(function (resp) { return resp.json(); });
    if (!timer) return withTimeout(p, FETCH_TIMEOUT_MS, "fetch_timeout");
    return p.then(
      function (v) { clearTimeout(timer); return v; },
      function (e) { clearTimeout(timer); throw e; }
    );
  }

  /* ---------- DOM-flow ---------- */

  function init() {
    var root = document.getElementById("maatcode-foto");
    if (!root) return;
    var endpoint = root.dataset.endpoint;
    var sitekey = root.dataset.sitekey;
    if (!endpoint || !sitekey) return;

    var input = document.getElementById("maatcode-foto-input");
    var button = document.getElementById("maatcode-foto-button");
    var turnstileEl = document.getElementById("maatcode-foto-turnstile");
    var statusEl = document.getElementById("maatcode-foto-status");
    var resultEl = document.getElementById("maatcode-foto-result");
    if (!input || !button || !turnstileEl || !statusEl || !resultEl) return;

    var busy = false;

    function setStatus(text) {
      statusEl.hidden = !text;
      statusEl.textContent = text || "";
    }

    function clearResult() {
      resultEl.hidden = true;
      resultEl.textContent = "";
    }

    function makeButton(label, className, onClick) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "button " + className + " maatcode-foto__result-button";
      btn.textContent = label;
      btn.addEventListener("click", onClick);
      return btn;
    }

    function makeContactLink(label, message) {
      var a = document.createElement("a");
      a.className = "button button--secondary maatcode-foto__result-button";
      a.href = "/pages/contact?onderwerp=productvraag";
      a.textContent = label;
      // De situatie meegeven aan de contactpagina zonder hem door de URL te sturen.
      a.addEventListener("click", function () {
        try { sessionStorage.setItem("hdr:contact-prefill", message); } catch (err) {}
      });
      return a;
    }

    function resetToStart() {
      clearResult();
      setStatus("");
      input.value = "";
      button.hidden = false;
    }

    function focusManualForm() {
      var form = document.getElementById("maatfinder-form");
      if (form) {
        form.scrollIntoView({ behavior: "smooth", block: "start" });
        var breedte = form.querySelector("[name='breedte']");
        if (breedte) setTimeout(function () { breedte.focus(); }, 400);
      }
    }

    function showFallback(reason, result) {
      clearResult();
      setStatus("");
      button.hidden = true;
      var tekst = fallbackTekst(reason, result);

      var intro = document.createElement("p");
      intro.className = "maatcode-foto__result-text";
      intro.textContent = tekst.intro;
      resultEl.appendChild(intro);

      if (tekst.tip) {
        var tip = document.createElement("p");
        tip.className = "maatcode-foto__result-tip";
        tip.textContent = tekst.tip;
        resultEl.appendChild(tip);
      }

      var actions = document.createElement("div");
      actions.className = "maatcode-foto__result-actions";
      actions.appendChild(makeButton("Probeer een nieuwe foto", "button--primary", function () {
        resetToStart();
        input.click();
      }));
      actions.appendChild(makeButton("Vul je maat handmatig in", "button--secondary", function () {
        resetToStart();
        focusManualForm();
      }));
      var om = result && result.omgerekend;
      actions.appendChild(makeContactLink(
        "Vraag ons om hulp",
        om
          ? "Hoi! Op mijn typeplaatje staat " + vanLabel(om) + ". Welk dakraam past daarvoor in de plaats?"
          : "Hoi! Kunnen jullie de juiste maat opzoeken? De fotoherkenning kwam er niet uit."
      ));
      resultEl.appendChild(actions);
      resultEl.hidden = false;

      trackEvent("maatcode_foto_resultaat", {
        uitkomst: "fallback",
        reden: reason || "onbekend",
        van_merk: om ? om.van_merk : undefined,
        // Cloudflare-code (300*/600* = bot vermoed, 110600 = timeout): zonder deze code is een
        // geblokkeerde echte klant niet te onderscheiden van een trage check.
        turnstile_fout: reason === "turnstile" && turnstileState.lastError ? turnstileState.lastError : undefined,
      });
    }

    function showOutsideAssortiment(result) {
      clearResult();
      button.hidden = true;

      var text = document.createElement("p");
      text.className = "maatcode-foto__result-text";
      text.textContent = "Maatcode " + result.maatcode + " valt buiten ons standaard-assortiment. Neem contact op, dan kijken we samen naar de mogelijkheden.";
      resultEl.appendChild(text);

      var actions = document.createElement("div");
      actions.className = "maatcode-foto__result-actions";
      actions.appendChild(makeContactLink(
        "Vraag ons om hulp",
        "Hoi! De fotoherkenning leest maatcode " + result.maatcode + " op mijn typeplaatje, maar die zit niet in jullie standaard-assortiment. Kunnen jullie meedenken?"
      ));
      actions.appendChild(makeButton("Vul je maat handmatig in", "button--secondary", function () {
        resetToStart();
        focusManualForm();
      }));
      resultEl.appendChild(actions);
      resultEl.hidden = false;
    }

    function showConfirm(result) {
      clearResult();
      setStatus("");
      button.hidden = true;

      var merkwissel = isMerkwissel(result);
      var text = document.createElement("p");
      text.className = "maatcode-foto__result-text";
      if (merkwissel) {
        var mw = merkwisselTekst(result);
        text.textContent = mw.vraag;
        resultEl.appendChild(text);
        var gootstuk = document.createElement("p");
        gootstuk.className = "maatcode-foto__result-tip";
        gootstuk.textContent = mw.gootstuk;
        resultEl.appendChild(gootstuk);
      } else {
        text.appendChild(document.createTextNode("Wij lezen: "));
        var strong = document.createElement("strong");
        strong.textContent = resultLabel(result);
        text.appendChild(strong);
        text.appendChild(document.createTextNode(". Klopt dit?"));
        resultEl.appendChild(text);
      }

      var actions = document.createElement("div");
      actions.className = "maatcode-foto__result-actions";
      var jaLabel = merkwissel ? "Ja, toon " + resultLabel(result) : "Ja, toon passende dakramen";
      actions.appendChild(makeButton(jaLabel, "button--primary", function () {
        trackEvent("maatcode_foto_bevestigd", {
          klopt: "ja",
          maatcode: result.maatcode,
          in_assortiment: result.inAssortiment === true,
          van_merk: merkwissel ? result.omgerekend.van_merk : undefined,
        });
        if (result.inAssortiment === true && canRouteDirect(result)) {
          window.location.href = collectionUrl(result);
        } else {
          showOutsideAssortiment(result);
        }
      }));
      actions.appendChild(makeButton("Nee, probeer het opnieuw", "button--secondary", function () {
        trackEvent("maatcode_foto_bevestigd", { klopt: "nee", maatcode: result.maatcode });
        resetToStart();
        input.click();
      }));
      resultEl.appendChild(actions);
      resultEl.hidden = false;

      trackEvent("maatcode_foto_resultaat", {
        uitkomst: "confirm",
        maatcode: result.maatcode,
        confidence: result.confidence,
        in_assortiment: result.inAssortiment === true,
        van_merk: merkwissel ? result.omgerekend.van_merk : undefined,
      });
    }

    function handleResult(result) {
      var route = decideRoute(result);
      if (route.action === "direct") {
        setStatus("Gevonden: " + resultLabel(result) + ". We brengen je naar de passende dakramen.");
        trackEvent("maatcode_foto_resultaat", {
          uitkomst: "direct",
          maatcode: result.maatcode,
          confidence: result.confidence,
        });
        setTimeout(function () { window.location.href = route.url; }, 600);
      } else if (route.action === "confirm") {
        showConfirm(result);
      } else {
        showFallback(route.reason, result);
      }
    }

    function handleFile(file) {
      if (busy || !file) return;
      busy = true;
      clearResult();
      trackEvent("maatcode_foto_started", { bestand_type: file.type || "onbekend" });

      if (file.size > MAX_FILE_BYTES) {
        busy = false;
        showFallback("bestand_te_groot");
        return;
      }

      setStatus("Foto verkleinen…");
      // Turnstile-challenge en foto-verkleining lopen parallel; de hele
      // voorbereiding is hard getimeboxt zodat de gebruiker nooit zonder
      // fallback-knoppen blijft hangen (Codex HIGH-fix).
      var widgetReady = ensureTurnstileWidget(turnstileEl, sitekey);
      var imagePromise = fileToJpegBase64(file);
      // Foto klaar maar nog geen token: laat zien waar we op wachten, anders lijkt het vast te zitten.
      imagePromise.then(function () {
        if (busy && !currentToken()) setStatus("Beveiligingscheck… dit duurt soms even");
      }, function () { /* de catch hieronder toont de fallback */ });

      withTimeout(
        Promise.all([imagePromise, widgetReady.then(getTurnstileToken)]),
        PREP_TIMEOUT_MS,
        "voorbereiding_timeout"
      )
        .then(function (parts) {
          var base64 = parts[0];
          var token = parts[1];
          if (!token) throw Object.assign(new Error("turnstile"), { reason: "turnstile" });
          setStatus("Code lezen…");
          return postFoto(endpoint, base64, token);
        })
        .then(function (result) {
          consumeTurnstileToken();
          busy = false;
          handleResult(result);
        })
        .catch(function (err) {
          consumeTurnstileToken();
          busy = false;
          // Kon het Turnstile-script niet laden (adblocker, bedrijfsnetwerk), dan is de foto ook
          // nooit verstuurd: zelfde eerlijke melding als een mislukte check, geen fototip.
          var reden = err && err.reason ? err.reason
            : err && err.message === "turnstile_script" ? "turnstile" : "netwerk_of_decode";
          if (reden === "turnstile" && !turnstileState.lastError && err && err.message === "turnstile_script") {
            turnstileState.lastError = "script_geblokkeerd";
          }
          showFallback(reden);
        });
    }

    // Start de Turnstile-check zodra de widget in beeld komt: een koud token duurt 12-25+ s
    // (gemeten 18-09), en zo loopt dat terwijl de klant nog een foto kiest.
    if (typeof IntersectionObserver !== "undefined") {
      var vooraf = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        vooraf.disconnect();
        ensureTurnstileWidget(turnstileEl, sitekey).catch(function () { /* fallback vangt dit bij submit */ });
      }, { rootMargin: "200px" });
      vooraf.observe(root);
    }

    button.addEventListener("click", function () {
      // Turnstile alvast laden terwijl de file-picker open staat.
      ensureTurnstileWidget(turnstileEl, sitekey).catch(function () { /* fallback vangt dit bij submit */ });
      input.click();
    });

    input.addEventListener("change", function () {
      if (input.files && input.files[0]) handleFile(input.files[0]);
    });
  }

  // Expose voor unit-tests van de pure beslislogica
  if (typeof window !== "undefined") {
    window.__hetdakraamMaatcodeFoto = {
      decideRoute: decideRoute,
      collectionUrl: collectionUrl,
      canRouteDirect: canRouteDirect,
      resultLabel: resultLabel,
      merkwisselTekst: merkwisselTekst,
      fallbackTekst: fallbackTekst,
    };
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
    } else {
      init();
    }
  }
})();
